import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import SettingSection from "../SettingSection";
import SettingRow from "../SettingRow";
import ExpandableDescription from "../ExpandableDescription";
import { ShapeFillIcon, UploadIcon } from "../../../../icons";
import { PYTHON_BACKEND_URL } from "../../../../constants/backend";
const PYTHON_BACKEND_BASE_URL = PYTHON_BACKEND_URL;
const SEMANTIC_COLOR_MODE_CHOICES = [
    { key: "on", label: "ON" },
    { key: "empty-only", label: "EMPTY CELLS ONLY" },
    { key: "off", label: "OFF" },
];
const DEFAULT_FONT_COLOR_SETTINGS = {
    excelHardcodedFontColor: "#0000FF",
    excelCalculationFontColor: "#000000",
    excelReferenceOtherSheetFontColor: "#1E914E",
    excelReferenceOtherWorkbookFontColor: "#8B0000",
    excelReferenceExternalDataFontColor: "#E30000",
};
const COLOR_SETTING_ROWS = [
    {
        key: "excelHardcodedFontColor",
        label: "Hardcoded Cell Font Color",
        description: "Used for hardcoded numeric values.",
    },
    {
        key: "excelCalculationFontColor",
        label: "Formula Cell Font Color",
        description: "Used for local calculations and same-sheet formulas.",
    },
    {
        key: "excelReferenceOtherSheetFontColor",
        label: "Sheet Reference Font Color",
        description: "Used for formulas that reference another sheet in this workbook.",
    },
    {
        key: "excelReferenceOtherWorkbookFontColor",
        label: "Workbook Reference Font Color",
        description: "Used for formulas that reference other workbooks.",
    },
    {
        key: "excelReferenceExternalDataFontColor",
        label: "External Data Reference Font Color",
        description: "Used for formulas that reference external providers (Bloomberg, Capital IQ, FactSet).",
    },
];
const SWATCH_COLORS = [
    "#000000",
    "#666666",
    "#0000FF",
    "#1E5EFF",
    "#1E914E",
    "#8B0000",
    "#E30000",
    "#F59E0B",
    "#B45309",
    "#DB2777",
];
const SCAN_KEY_TO_CONFIG_KEY = {
    hardcoded_font_color: "excelHardcodedFontColor",
    calculation_font_color: "excelCalculationFontColor",
    reference_other_sheet_font_color: "excelReferenceOtherSheetFontColor",
    reference_other_workbook_font_color: "excelReferenceOtherWorkbookFontColor",
    reference_external_data_font_color: "excelReferenceExternalDataFontColor",
};
function normalizeHexColor(input, fallback) {
    if (typeof input !== "string") {
        return fallback;
    }
    const raw = input.trim();
    const candidate = raw.startsWith("#") ? raw.slice(1) : raw;
    if (!/^[0-9A-Fa-f]{6}$/.test(candidate)) {
        return fallback;
    }
    return `#${candidate.toUpperCase()}`;
}
function tryNormalizeHexColor(input) {
    if (typeof input !== "string") {
        return null;
    }
    const raw = input.trim();
    const candidate = raw.startsWith("#") ? raw.slice(1) : raw;
    if (!/^[0-9A-Fa-f]{6}$/.test(candidate)) {
        return null;
    }
    return `#${candidate.toUpperCase()}`;
}
function clampColorChannel(value) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.max(0, Math.min(255, Math.round(value)));
}
function clampUnitInterval(value) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.max(0, Math.min(1, value));
}
function clampHue(value) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    const normalized = value % 360;
    return normalized < 0 ? normalized + 360 : normalized;
}
function hexToRgb(colorHex) {
    const normalized = normalizeHexColor(colorHex, "#000000");
    return {
        r: parseInt(normalized.slice(1, 3), 16),
        g: parseInt(normalized.slice(3, 5), 16),
        b: parseInt(normalized.slice(5, 7), 16),
    };
}
function rgbToHex(rgb) {
    const toHexPair = (value) => clampColorChannel(value).toString(16).toUpperCase().padStart(2, "0");
    return `#${toHexPair(rgb.r)}${toHexPair(rgb.g)}${toHexPair(rgb.b)}`;
}
function rgbToHsv(rgb) {
    const r = clampColorChannel(rgb.r) / 255;
    const g = clampColorChannel(rgb.g) / 255;
    const b = clampColorChannel(rgb.b) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    let h = 0;
    if (delta !== 0) {
        if (max === r) {
            h = 60 * (((g - b) / delta) % 6);
        }
        else if (max === g) {
            h = 60 * ((b - r) / delta + 2);
        }
        else {
            h = 60 * ((r - g) / delta + 4);
        }
    }
    if (h < 0) {
        h += 360;
    }
    const s = max === 0 ? 0 : delta / max;
    const v = max;
    return { h, s, v };
}
function hsvToRgb(hsv) {
    const h = clampHue(hsv.h);
    const s = clampUnitInterval(hsv.s);
    const v = clampUnitInterval(hsv.v);
    const chroma = v * s;
    const hueSegment = h / 60;
    const secondLargestComponent = chroma * (1 - Math.abs((hueSegment % 2) - 1));
    const match = v - chroma;
    let rPrime = 0;
    let gPrime = 0;
    let bPrime = 0;
    if (hueSegment >= 0 && hueSegment < 1) {
        rPrime = chroma;
        gPrime = secondLargestComponent;
    }
    else if (hueSegment >= 1 && hueSegment < 2) {
        rPrime = secondLargestComponent;
        gPrime = chroma;
    }
    else if (hueSegment >= 2 && hueSegment < 3) {
        gPrime = chroma;
        bPrime = secondLargestComponent;
    }
    else if (hueSegment >= 3 && hueSegment < 4) {
        gPrime = secondLargestComponent;
        bPrime = chroma;
    }
    else if (hueSegment >= 4 && hueSegment < 5) {
        rPrime = secondLargestComponent;
        bPrime = chroma;
    }
    else {
        rPrime = chroma;
        bPrime = secondLargestComponent;
    }
    return {
        r: clampColorChannel((rPrime + match) * 255),
        g: clampColorChannel((gPrime + match) * 255),
        b: clampColorChannel((bPrime + match) * 255),
    };
}
function hsvToHex(hsv) {
    return rgbToHex(hsvToRgb(hsv));
}
function getContrastRingColor(colorHex) {
    const rgb = hexToRgb(colorHex);
    const toLinear = (channel) => {
        const normalized = channel / 255;
        if (normalized <= 0.03928) {
            return normalized / 12.92;
        }
        return Math.pow((normalized + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * toLinear(rgb.r) +
        0.7152 * toLinear(rgb.g) +
        0.0722 * toLinear(rgb.b);
    return luminance > 0.45 ? "#000000" : "#FFFFFF";
}
async function captureCurrentWorkbookBase64(captureWorkbookSnapshotBase64) {
    if (!captureWorkbookSnapshotBase64) {
        throw new Error("Workbook capture utility is unavailable in this host.");
    }
    const workbookBase64 = await captureWorkbookSnapshotBase64();
    if (!workbookBase64) {
        throw new Error("Failed to capture active workbook.");
    }
    return workbookBase64;
}
function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode(...chunk);
    }
    return window.btoa(binary);
}
function formatErrorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    return String(error);
}
const InlineColorPicker = ({ colorHex, onCommit }) => {
    const normalizedColor = React.useMemo(() => normalizeHexColor(colorHex, "#000000"), [colorHex]);
    const normalizedHsv = React.useMemo(() => rgbToHsv(hexToRgb(normalizedColor)), [normalizedColor]);
    const [draftColor, setDraftColor] = React.useState(normalizedColor);
    const [draftHue, setDraftHue] = React.useState(normalizedHsv.h);
    const hsvFromDraft = React.useMemo(() => rgbToHsv(hexToRgb(draftColor)), [draftColor]);
    const hsv = React.useMemo(() => ({
        ...hsvFromDraft,
        h: hsvFromDraft.s > 0.0001 && hsvFromDraft.v > 0.0001
            ? hsvFromDraft.h
            : draftHue,
    }), [draftHue, hsvFromDraft]);
    const pickerRingColor = React.useMemo(() => getContrastRingColor(draftColor), [draftColor]);
    const hueRingColor = React.useMemo(() => getContrastRingColor(hsvToHex({ h: hsv.h, s: 1, v: 1 })), [hsv.h]);
    const saturationRef = React.useRef(null);
    const latestColorRef = React.useRef(draftColor);
    const commitTimeoutRef = React.useRef(null);
    React.useEffect(() => {
        setDraftColor(normalizedColor);
        latestColorRef.current = normalizedColor;
        if (normalizedHsv.s > 0.0001 && normalizedHsv.v > 0.0001) {
            setDraftHue(normalizedHsv.h);
        }
    }, [normalizedColor, normalizedHsv.h, normalizedHsv.s, normalizedHsv.v]);
    React.useEffect(() => {
        if (hsvFromDraft.s > 0.0001 && hsvFromDraft.v > 0.0001) {
            setDraftHue((current) => {
                if (Math.abs(current - hsvFromDraft.h) < 0.01) {
                    return current;
                }
                return hsvFromDraft.h;
            });
        }
    }, [hsvFromDraft.h, hsvFromDraft.s, hsvFromDraft.v]);
    const clearCommitTimeout = React.useCallback(() => {
        if (commitTimeoutRef.current !== null) {
            window.clearTimeout(commitTimeoutRef.current);
            commitTimeoutRef.current = null;
        }
    }, []);
    React.useEffect(() => {
        return () => {
            clearCommitTimeout();
        };
    }, [clearCommitTimeout]);
    const commitNow = React.useCallback((nextHex) => {
        const normalized = normalizeHexColor(nextHex ?? latestColorRef.current, latestColorRef.current);
        latestColorRef.current = normalized;
        setDraftColor(normalized);
        clearCommitTimeout();
        onCommit(normalized);
    }, [clearCommitTimeout, onCommit]);
    const previewAndScheduleCommit = React.useCallback((nextHex) => {
        const normalized = normalizeHexColor(nextHex, latestColorRef.current);
        latestColorRef.current = normalized;
        setDraftColor(normalized);
        clearCommitTimeout();
        commitTimeoutRef.current = window.setTimeout(() => {
            onCommit(latestColorRef.current);
        }, 120);
    }, [clearCommitTimeout, onCommit]);
    const updateSaturationValue = React.useCallback((clientX, clientY) => {
        const area = saturationRef.current;
        if (!area) {
            return;
        }
        const rect = area.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return;
        }
        const nextSaturation = clampUnitInterval((clientX - rect.left) / rect.width);
        const nextValue = clampUnitInterval(1 - (clientY - rect.top) / rect.height);
        previewAndScheduleCommit(hsvToHex({ h: hsv.h, s: nextSaturation, v: nextValue }));
    }, [hsv.h, previewAndScheduleCommit]);
    const handleSaturationPointerDown = React.useCallback((event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        updateSaturationValue(event.clientX, event.clientY);
    }, [updateSaturationValue]);
    const handleSaturationPointerMove = React.useCallback((event) => {
        if ((event.buttons & 1) !== 1 && event.pointerType === "mouse") {
            return;
        }
        updateSaturationValue(event.clientX, event.clientY);
    }, [updateSaturationValue]);
    const handleSaturationPointerUp = React.useCallback((event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        commitNow();
    }, [commitNow]);
    const handleSaturationPointerCancel = React.useCallback((event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        commitNow();
    }, [commitNow]);
    const handleHueChange = React.useCallback((event) => {
        const nextHue = clampHue(Number(event.target.value));
        setDraftHue(nextHue);
        previewAndScheduleCommit(hsvToHex({ h: nextHue, s: hsv.s, v: hsv.v }));
    }, [hsv.s, hsv.v, previewAndScheduleCommit]);
    const hueSliderStyle = React.useMemo(() => ({
        background: "linear-gradient(to right, #FF0000 0%, #FFFF00 17%, #00FF00 33%, #00FFFF 50%, #0000FF 67%, #FF00FF 83%, #FF0000 100%)",
        "--carousel-slider-thumb-ring": hueRingColor,
    }), [hueRingColor]);
    return (_jsxs("div", { className: "rounded-lg border border-gray-200 bg-gray-50 p-3", children: [_jsxs("div", { className: "mb-2 flex items-center gap-2", children: [_jsx("div", { className: "h-8 w-8 rounded-md border border-gray-200", style: { backgroundColor: normalizedColor }, "aria-hidden": "true" }), _jsx("div", { className: "text-[13px] font-bold text-gray-800", children: normalizedColor })] }), _jsxs("div", { ref: saturationRef, onPointerDown: handleSaturationPointerDown, onPointerMove: handleSaturationPointerMove, onPointerUp: handleSaturationPointerUp, onPointerCancel: handleSaturationPointerCancel, className: "relative h-[132px] w-full cursor-crosshair overflow-hidden rounded-md border border-gray-300", style: { backgroundColor: `hsl(${Math.round(hsv.h)}, 100%, 50%)` }, "aria-label": "Saturation and brightness picker", children: [_jsx("div", { className: "pointer-events-none absolute inset-0 bg-gradient-to-r from-white to-transparent" }), _jsx("div", { className: "pointer-events-none absolute inset-0 bg-gradient-to-t from-black to-transparent" }), _jsx("div", { className: "pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border bg-transparent", style: {
                            left: `${hsv.s * 100}%`,
                            top: `${(1 - hsv.v) * 100}%`,
                            borderColor: pickerRingColor,
                        }, "aria-hidden": "true" })] }), _jsx("input", { type: "range", min: 0, max: 360, value: Math.round(hsv.h), onChange: handleHueChange, onMouseUp: () => commitNow(), onTouchEnd: () => commitNow(), onPointerUp: () => commitNow(), className: "carousel-hue-slider mt-2 h-2 w-full cursor-pointer appearance-none rounded", style: hueSliderStyle, "aria-label": "Hue slider" })] }));
};
const ExcelFormattingTab = ({ config, updateConfig, captureWorkbookSnapshotBase64, }) => {
    const [isScanning, setIsScanning] = React.useState(false);
    const [scanMessage, setScanMessage] = React.useState("");
    const [scanError, setScanError] = React.useState("");
    const fileInputRef = React.useRef(null);
    const [expandedColorKey, setExpandedColorKey] = React.useState(null);
    const [hexInputValues, setHexInputValues] = React.useState({
        excelHardcodedFontColor: normalizeHexColor(config.excelHardcodedFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelHardcodedFontColor),
        excelCalculationFontColor: normalizeHexColor(config.excelCalculationFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelCalculationFontColor),
        excelReferenceOtherSheetFontColor: normalizeHexColor(config.excelReferenceOtherSheetFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelReferenceOtherSheetFontColor),
        excelReferenceOtherWorkbookFontColor: normalizeHexColor(config.excelReferenceOtherWorkbookFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelReferenceOtherWorkbookFontColor),
        excelReferenceExternalDataFontColor: normalizeHexColor(config.excelReferenceExternalDataFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelReferenceExternalDataFontColor),
    });
    const isExcelHost = React.useMemo(() => {
        const office = window.Office;
        const host = office?.context?.host;
        if (!host) {
            return false;
        }
        return String(host).toLowerCase() === "excel";
    }, []);
    const setHexInputValue = React.useCallback((key, value) => {
        setHexInputValues((current) => {
            if (current[key] === value) {
                return current;
            }
            return {
                ...current,
                [key]: value,
            };
        });
    }, []);
    const updateColorSetting = React.useCallback((key, colorHex) => {
        const normalized = normalizeHexColor(colorHex, DEFAULT_FONT_COLOR_SETTINGS[key]);
        updateConfig(key, normalized);
        setHexInputValues((current) => {
            if (current[key] === normalized) {
                return current;
            }
            return {
                ...current,
                [key]: normalized,
            };
        });
    }, [updateConfig]);
    React.useEffect(() => {
        setHexInputValues((current) => {
            const nextValues = {
                excelHardcodedFontColor: normalizeHexColor(config.excelHardcodedFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelHardcodedFontColor),
                excelCalculationFontColor: normalizeHexColor(config.excelCalculationFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelCalculationFontColor),
                excelReferenceOtherSheetFontColor: normalizeHexColor(config.excelReferenceOtherSheetFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelReferenceOtherSheetFontColor),
                excelReferenceOtherWorkbookFontColor: normalizeHexColor(config.excelReferenceOtherWorkbookFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelReferenceOtherWorkbookFontColor),
                excelReferenceExternalDataFontColor: normalizeHexColor(config.excelReferenceExternalDataFontColor, DEFAULT_FONT_COLOR_SETTINGS.excelReferenceExternalDataFontColor),
            };
            const changed = Object.keys(nextValues).some((key) => current[key] !== nextValues[key]);
            return changed ? nextValues : current;
        });
    }, [
        config.excelCalculationFontColor,
        config.excelHardcodedFontColor,
        config.excelReferenceExternalDataFontColor,
        config.excelReferenceOtherSheetFontColor,
        config.excelReferenceOtherWorkbookFontColor,
    ]);
    const semanticColorMode = React.useMemo(() => {
        if (!config.isExcelSemanticFormattingEnabled) {
            return "off";
        }
        return config.excelApplyColorsOnlyToEmptyCells ? "empty-only" : "on";
    }, [config.excelApplyColorsOnlyToEmptyCells, config.isExcelSemanticFormattingEnabled]);
    const handleSemanticColorModeChange = React.useCallback((mode) => {
        if (mode === "off") {
            updateConfig("isExcelSemanticFormattingEnabled", false);
            return;
        }
        updateConfig("isExcelSemanticFormattingEnabled", true);
        updateConfig("excelApplyColorsOnlyToEmptyCells", (mode === "empty-only"));
    }, [updateConfig]);
    const handleHexInputChange = React.useCallback((key, rawValue) => {
        setExpandedColorKey(key);
        setHexInputValue(key, rawValue);
    }, [setHexInputValue]);
    const handleHexInputCommit = React.useCallback((key, rawValue, fallback) => {
        updateColorSetting(key, normalizeHexColor(rawValue, fallback));
    }, [updateColorSetting]);
    const requestColorScan = React.useCallback(async (payload) => {
        const response = await fetch(`${PYTHON_BACKEND_BASE_URL}/api/settings/scan-workbook-colors`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                workbook_base64: payload.workbookBase64,
                workbook_name: payload.workbookName,
                source: payload.source,
                update_colors: true,
            }),
        });
        const data = (await response.json());
        if (!response.ok || data.success === false) {
            throw new Error(data.error || `Workbook color scan failed (${response.status}).`);
        }
        return data;
    }, []);
    const applyScanResult = React.useCallback((result) => {
        const detected = result.detected_colors || {};
        let applied = 0;
        Object.entries(detected).forEach(([scanKey, colorHex]) => {
            const configKey = SCAN_KEY_TO_CONFIG_KEY[scanKey];
            if (!configKey || typeof colorHex !== "string") {
                return;
            }
            updateColorSetting(configKey, colorHex);
            applied += 1;
        });
        const skipped = result.skipped_colors?.length ? `Skipped: ${result.skipped_colors.join(", ")}.` : "";
        const scanned = typeof result.total_non_empty_cells === "number" ? `${result.total_non_empty_cells} non-empty cells scanned.` : "";
        setScanMessage(applied > 0
            ? `Applied ${applied} detected font-color setting(s). ${scanned} ${skipped}`.trim()
            : `No qualifying workbook font colors were detected. ${scanned} ${skipped}`.trim());
    }, [updateColorSetting]);
    const handleDetectFromWorkbook = React.useCallback(async () => {
        setIsScanning(true);
        setScanError("");
        setScanMessage("");
        try {
            const workbookBase64 = await captureCurrentWorkbookBase64(captureWorkbookSnapshotBase64);
            const result = await requestColorScan({
                workbookBase64,
                source: "current",
            });
            applyScanResult(result);
        }
        catch (error) {
            setScanError(formatErrorMessage(error));
        }
        finally {
            setIsScanning(false);
        }
    }, [applyScanResult, captureWorkbookSnapshotBase64, requestColorScan]);
    const handleUploadWorkbookClick = React.useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    const handleUploadWorkbookChange = React.useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }
        setIsScanning(true);
        setScanError("");
        setScanMessage("");
        try {
            const buffer = await file.arrayBuffer();
            const workbookBase64 = arrayBufferToBase64(buffer);
            const result = await requestColorScan({
                workbookBase64,
                workbookName: file.name,
                source: "upload",
            });
            applyScanResult(result);
        }
        catch (error) {
            setScanError(formatErrorMessage(error));
        }
        finally {
            setIsScanning(false);
        }
    }, [applyScanResult, requestColorScan]);
    const handleResetToDefault = React.useCallback(() => {
        updateConfig("isExcelSemanticFormattingEnabled", true);
        updateConfig("excelApplyColorsOnlyToEmptyCells", true);
        updateColorSetting("excelHardcodedFontColor", DEFAULT_FONT_COLOR_SETTINGS.excelHardcodedFontColor);
        updateColorSetting("excelCalculationFontColor", DEFAULT_FONT_COLOR_SETTINGS.excelCalculationFontColor);
        updateColorSetting("excelReferenceOtherSheetFontColor", DEFAULT_FONT_COLOR_SETTINGS.excelReferenceOtherSheetFontColor);
        updateColorSetting("excelReferenceOtherWorkbookFontColor", DEFAULT_FONT_COLOR_SETTINGS.excelReferenceOtherWorkbookFontColor);
        updateColorSetting("excelReferenceExternalDataFontColor", DEFAULT_FONT_COLOR_SETTINGS.excelReferenceExternalDataFontColor);
        setScanError("");
        setScanMessage("Restored default Excel formatting colors.");
    }, [updateColorSetting, updateConfig]);
    return (_jsxs(SettingSection, { title: "", children: [_jsx(SettingRow, { label: "Semantic Color Application Mode", control: _jsx("div", { className: "inline-flex gap-2", children: SEMANTIC_COLOR_MODE_CHOICES.map((choice) => {
                        const isActive = semanticColorMode === choice.key;
                        return (_jsx("button", { type: "button", onClick: () => handleSemanticColorModeChange(choice.key), className: `flex h-[31px] items-center justify-center rounded-full px-4 text-[13px] font-bold leading-[150%] font-sans transition-all duration-200 ${isActive
                                ? "bg-primary-purple-10 text-primary-purple-70"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`, children: choice.label }, choice.key));
                    }) }) }), _jsxs(ExpandableDescription, { children: ["These settings control finance-style semantic font coloring when Carousel writes values or formulas into Excel.", _jsx("br", {}), _jsx("br", {}), "Use ", _jsx("strong", { children: "ON" }), " to always apply semantic colors, ", _jsx("strong", { children: "EMPTY CELLS ONLY" }), " to preserve existing styled cells, or ", _jsx("strong", { children: "OFF" }), " to disable semantic coloring."] }), _jsxs("div", { className: "mb-4 rounded-lg border border-gray-300 bg-primary-purple-10 p-4", children: [_jsx("div", { className: "text-[15px] font-bold text-primary-purple-80", children: "Automate Color Selection" }), _jsx("p", { className: "mt-1 text-[13px] text-gray-600", children: "Scan a workbook and apply the detected color conventions." }), _jsxs("div", { className: "mt-3 grid grid-cols-2 gap-3", children: [_jsx("button", { type: "button", onClick: handleDetectFromWorkbook, disabled: isScanning || !isExcelHost, className: "flex h-[36px] w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-[13px] font-bold text-gray-800 transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60", title: !isExcelHost ? "Current workbook detection is only available in Excel." : undefined, children: isScanning ? "Scanning..." : "Detect from Workbook" }), _jsxs("button", { type: "button", onClick: handleUploadWorkbookClick, disabled: isScanning, className: "flex h-[36px] w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-[13px] font-bold text-gray-800 transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60", children: [_jsx(UploadIcon, { size: 16, className: "text-gray-700" }), isScanning ? "Scanning..." : "Upload a Workbook"] }), _jsx("input", { ref: fileInputRef, type: "file", accept: ".xlsx,.xlsm,.xltx,.xltm", className: "hidden", onChange: handleUploadWorkbookChange })] }), !isExcelHost && (_jsx("p", { className: "mt-2 text-[12px] text-gray-600", children: "Current-workbook scan is disabled outside Excel. Upload scan still works." })), scanMessage && _jsx("p", { className: "mt-2 text-[12px] text-green-80", children: scanMessage }), scanError && _jsx("p", { className: "mt-2 text-[12px] text-red-80", children: scanError })] }), _jsx("div", { className: "space-y-3", children: COLOR_SETTING_ROWS.map((row) => {
                    const selectedColor = normalizeHexColor(config[row.key], DEFAULT_FONT_COLOR_SETTINGS[row.key]);
                    const hexInputValue = hexInputValues[row.key] || selectedColor;
                    const previewColor = tryNormalizeHexColor(hexInputValue) || selectedColor;
                    const isExpanded = expandedColorKey === row.key;
                    return (_jsxs("div", { className: "rounded-lg border border-gray-200 bg-white p-3", children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("h3", { className: "text-[15px] font-bold leading-[150%] text-gray-800", children: row.label }), _jsx("p", { className: "text-[13px] text-gray-600", children: row.description })] }), _jsxs("div", { className: "flex shrink-0 items-center gap-1", children: [_jsx("input", { type: "text", value: hexInputValue, onClick: () => setExpandedColorKey(row.key), onFocus: () => setExpandedColorKey(row.key), onChange: (event) => handleHexInputChange(row.key, event.target.value), onBlur: (event) => handleHexInputCommit(row.key, event.target.value, selectedColor), onKeyDown: (event) => {
                                                    if (event.key === "Enter") {
                                                        handleHexInputCommit(row.key, event.currentTarget.value, selectedColor);
                                                        event.currentTarget.blur();
                                                    }
                                                }, className: "h-7 w-[68px] rounded-[5px] border border-gray-200 bg-white px-1.5 text-[13px] font-bold leading-[150%] font-sans outline-none focus:border-primary-purple-70", style: { color: previewColor }, "aria-label": `${row.label} hex color` }), _jsx("button", { type: "button", onClick: () => setExpandedColorKey((current) => (current === row.key ? null : row.key)), className: `flex h-7 w-7 items-center justify-center rounded-lg border text-gray-700 transition-all duration-200 ${isExpanded
                                                    ? "border-gray-200 bg-gray-200"
                                                    : "border-gray-100 bg-gray-100 hover:bg-gray-200"}`, "aria-label": `Toggle color picker for ${row.label}`, children: _jsx(ShapeFillIcon, { size: 18, className: "text-gray-700" }) })] })] }), isExpanded && (_jsx("div", { className: "mt-2", children: _jsx(InlineColorPicker, { colorHex: previewColor, onCommit: (nextColor) => updateColorSetting(row.key, nextColor) }) })), _jsx("div", { className: "mt-2 flex flex-wrap items-center gap-2", children: SWATCH_COLORS.map((swatchColor) => {
                                    const normalized = normalizeHexColor(swatchColor, "#000000");
                                    const isSelected = selectedColor.toUpperCase() === normalized.toUpperCase();
                                    return (_jsx("button", { type: "button", onClick: () => updateColorSetting(row.key, normalized), className: `flex h-[30px] w-[35px] items-center justify-center rounded-[5px] border border-gray-200 bg-white transition-all duration-200 hover:border-primary-purple-70 ${isSelected ? "ring-2 ring-primary-purple-70" : ""}`, "aria-label": `Set ${row.label} to ${normalized}`, children: _jsx("span", { className: "text-[15px] font-bold leading-none", style: { color: normalized }, children: "$" }) }, `${row.key}-${normalized}`));
                                }) })] }, row.key));
                }) }), _jsx("div", { className: "mt-4 flex items-center justify-end", children: _jsx("button", { type: "button", onClick: handleResetToDefault, className: "flex h-[36px] items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-[13px] font-bold text-gray-800 transition-all duration-200 hover:bg-gray-50", children: "Reset to Default" }) })] }));
};
export default ExcelFormattingTab;
